import { type ReactElement } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Download } from 'lucide-react';
import { useShadowAiOrgStats } from '@/hooks/api/useShadowAiTools';
import { exportShadowAiToolsCsv } from '@kirin/api-client/api/shadow-ai';
import { apiClient } from '@/lib/api-client';
import { useToast } from '@/hooks/use-toast';
import { getErrorMessage } from '@/hooks/api/errorHelpers';
import { ShadowAiToolsTabs } from '@/components/shared/shadow-ai';

import { OrgSummaryStats } from './OrgSummaryStats';
import { DataTableBrowserAiToolsContent } from './DataTableBrowserAiToolsTab';
import { InstalledToolsSection } from './InstalledToolsSection';
import { DataTableRunningNowContent } from './DataTableRunningNowTab';
import { UserDrillDown } from './UserDrillDown';
import {
  useOrgShadowAiState,
  type ShadowAiTabValue,
} from './useOrgShadowAiState';

/**
 * Shadow AI → Tools page. The tool counter widgets sit on top, followed by one
 * internal tab per table (Browser AI Tools / Installed AI Tools / Running Now).
 * Network Discovery lives on its own sidebar sub-page, not here.
 */
export function ShadowAiToolsPage({
  organizationId,
}: {
  organizationId: string;
}): ReactElement {
  const [searchParams] = useSearchParams();
  const [state, actions] = useOrgShadowAiState();
  const selectedUserId = searchParams.get('user');
  const { toast } = useToast();

  const { data: statsData, isLoading: statsIsLoading } =
    useShadowAiOrgStats(organizationId);

  const handleExport = async (): Promise<void> => {
    try {
      const blob = await exportShadowAiToolsCsv(apiClient, organizationId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `shadow-ai-tools-${organizationId}-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Export failed',
        description: getErrorMessage(error),
      });
    }
  };

  if (selectedUserId) {
    return (
      <UserDrillDown
        organizationId={organizationId}
        userId={selectedUserId}
        onBack={actions.handleBackFromUser}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-end">
        <Button variant="outline" size="sm" onClick={handleExport}>
          <Download className="h-4 w-4 mr-2" />
          Export CSV
        </Button>
      </div>

      <OrgSummaryStats stats={statsData} isLoading={statsIsLoading} />

      <ShadowAiToolsTabs
        value={state.activeTab}
        onValueChange={v => actions.setActiveTab(v as ShadowAiTabValue)}
        browser={
          <DataTableBrowserAiToolsContent
            organizationId={organizationId}
            onUserClick={(userId, domain, userInfo) =>
              actions.handleUserClick(userId, domain, userInfo, 'browser')
            }
          />
        }
        installed={
          <InstalledToolsSection
            organizationId={organizationId}
            onUserClick={(userId, toolId, userInfo, category) =>
              actions.handleUserClick(
                userId,
                toolId,
                userInfo,
                'installed',
                undefined,
                category
              )
            }
          />
        }
        running={
          <DataTableRunningNowContent
            organizationId={organizationId}
            onUserClick={actions.handleRunningNowUserClick}
          />
        }
      />
    </div>
  );
}
